import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Admin.css';
import API_BASE from '../config';

function UserLifecycle() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [hiredCandidates, setHiredCandidates] = useState([]);
  const [onboardings, setOnboardings] = useState([]);
  const [offboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('onboarding');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    startDate: new Date().toISOString().slice(0, 10)
  });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch users
      const usersRes = await fetch(`${API_BASE}/admin/users`, { headers });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      // Fetch HIRED candidates (those with allDocumentsSigned)
      try {
        const candRes = await fetch(`${API_BASE}/recruitment/candidates?status=HIRED`, { headers });
        if (candRes.ok) {
          const data = await candRes.json();
          setHiredCandidates(data.candidates || []);
        }
      } catch { /* non-fatal */ }
      // Fetch active onboarding processes
      try {
        const onbRes = await fetch(`${API_BASE}/admin/onboarding`, { headers });
        if (onbRes.ok) {
          const data = await onbRes.json();
          setOnboardings(data.onboardings || data || []);
        }
      } catch { /* non-fatal */ }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateOnboarding = async (e) => {
    e.preventDefault();
    if (!formData.userId || !formData.startDate) {
      alert('Please fill in all fields');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/admin/onboarding`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: parseInt(formData.userId),
          startDate: new Date(formData.startDate).toISOString()
        })
      });
      if (response.ok) {
        setShowForm(false);
        setFormData({ userId: '', startDate: new Date().toISOString().slice(0, 10) });
        fetchData();
      } else {
        alert('Failed to create onboarding');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCreateOffboarding = async (e) => {
    e.preventDefault();
    if (!formData.userId || !formData.startDate) {
      alert('Please fill in all fields');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/admin/offboarding`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: parseInt(formData.userId),
          offboardDate: new Date(formData.startDate).toISOString()
        })
      });
      if (response.ok) {
        setShowForm(false);
        setFormData({ userId: '', startDate: new Date().toISOString().slice(0, 10) });
        fetchData();
      } else {
        alert('Failed to start offboarding');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getProgressPercentage = (checklist) => {
    if (!checklist || checklist.length === 0) return 0;
    const completed = checklist.filter(item => item.completed).length;
    return Math.round((completed / checklist.length) * 100);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'COMPLETED': return '#4caf50';
      case 'IN_PROGRESS': return '#2196f3';
      case 'NOT_STARTED': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  return (
    <div className="admin-container">
      <div className="page-header">
        <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 12 }}>← Back</button>
        <h1>User Lifecycle Management</h1>
        <p>Manage new hire pipeline, onboarding and offboarding workflows</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          🚀 New Hires – Pipeline
        </button>
        <button 
          className={`tab-button ${activeTab === 'offboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('offboarding')}
        >
          🚪 Offboarding
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form 
          onSubmit={activeTab === 'onboarding' ? handleCreateOnboarding : handleCreateOffboarding}
          className="form-section"
        >
          <h2>{activeTab === 'onboarding' ? 'Start New Hire Onboarding' : 'Start Offboarding'}</h2>
          
          <div className="form-group">
            <label>Select User</label>
            <select 
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              required
            >
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{activeTab === 'onboarding' ? 'Start Date' : 'Offboard Date'}</label>
            <input 
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>

          <div className="button-group">
            <button type="submit" className="btn-primary">Create</button>
            <button 
              type="button" 
              onClick={() => setShowForm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {!showForm && (
        <button 
          onClick={() => setShowForm(true)}
          className="btn-primary"
          style={{ marginBottom: '20px' }}
        >
          + Start New {activeTab === 'onboarding' ? 'Hire Onboarding' : 'Offboarding'}
        </button>
      )}

      {/* Content */}
      {activeTab === 'onboarding' ? (
        <div className="section">
          {/* ── HIRED candidates from Recruitment ── */}
          {hiredCandidates.length > 0 && (
            <>
              <h2 style={{ marginBottom: 12 }}>🎉 Hired Candidates – Awaiting Onboarding</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 28 }}>
                {hiredCandidates.map(c => (
                  <div key={c.id} style={{
                    background: '#f0fdf4', border: '2px solid #22c55e',
                    borderRadius: 10, padding: '14px 18px',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{c.fullName}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', margin: '4px 0' }}>{c.email}</div>
                    {c.campaign && <div style={{ fontSize: 12, background: '#dcfce7', color: '#15803d', display: 'inline-block', padding: '2px 8px', borderRadius: 10 }}>{c.campaign}</div>}
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
                      <span style={{ color: c.allDocumentsSigned ? '#15803d' : '#9ca3af' }}>
                        {c.allDocumentsSigned ? '✅ Docs Signed' : '⏳ Docs Pending'}
                      </span>
                      <span style={{ color: c.equipmentAssigned ? '#2563eb' : '#9ca3af' }}>
                        {c.equipmentAssigned ? '💻 Equipment Assigned' : '💻 No Equipment Yet'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h2>Active Onboarding Processes</h2>
          <div className="lifecycle-list">
            {onboardings && onboardings.length > 0 ? (
              onboardings.map(onboarding => (
                <div key={onboarding.id} className="lifecycle-card">
                  <div className="lifecycle-header">
                    <h3>{onboarding.user?.firstName} {onboarding.user?.lastName}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(onboarding.status) }}
                    >
                      {onboarding.status}
                    </span>
                  </div>

                  <div className="lifecycle-info">
                    <p><strong>Start Date:</strong> {new Date(onboarding.startDate).toLocaleDateString()}</p>
                    <p><strong>Email:</strong> {onboarding.user?.email}</p>
                    <p><strong>Department:</strong> {onboarding.user?.department?.name || 'N/A'}</p>
                  </div>

                  <div className="progress-section">
                    <div className="progress-header">
                      <span>Progress</span>
                      <span>{getProgressPercentage(onboarding.taskChecklist)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${getProgressPercentage(onboarding.taskChecklist)}%` }}
                      />
                    </div>

                    <div className="tasks-list">
                      {onboarding.taskChecklist && onboarding.taskChecklist.map((task, idx) => (
                        <div key={idx} className="task-item">
                          <input type="checkbox" checked={task.completed} readOnly />
                          <span>{task.task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No active onboardings</p>
            )}
          </div>
        </div>
      ) : (
        <div className="section">
          <h2>Active Offboarding Processes</h2>
          <div className="lifecycle-list">
            {offboardings && offboardings.length > 0 ? (
              offboardings.map(offboarding => (
                <div key={offboarding.id} className="lifecycle-card">
                  <div className="lifecycle-header">
                    <h3>{offboarding.user?.firstName} {offboarding.user?.lastName}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(offboarding.status) }}
                    >
                      {offboarding.status}
                    </span>
                  </div>

                  <div className="lifecycle-info">
                    <p><strong>Offboard Date:</strong> {new Date(offboarding.offboardDate).toLocaleDateString()}</p>
                    <p><strong>Email:</strong> {offboarding.user?.email}</p>
                    <p><strong>Access Revoked:</strong> {offboarding.accessRevoked ? '✓' : '✗'}</p>
                    <p><strong>Equipment Returned:</strong> {offboarding.equipmentReturned ? '✓' : '✗'}</p>
                    <p><strong>Final Audit Completed:</strong> {offboarding.finalAuditCompleted ? '✓' : '✗'}</p>
                  </div>

                  <div className="progress-section">
                    <div className="progress-header">
                      <span>Progress</span>
                      <span>{getProgressPercentage(offboarding.taskChecklist)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${getProgressPercentage(offboarding.taskChecklist)}%` }}
                      />
                    </div>

                    <div className="tasks-list">
                      {offboarding.taskChecklist && offboarding.taskChecklist.map((task, idx) => (
                        <div key={idx} className="task-item">
                          <input type="checkbox" checked={task.completed} readOnly />
                          <span>{task.task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No active offboardings</p>
            )}
          </div>
        </div>
      )}

      {/* Best Practices */}
      <div className="section info-section">
        <h2>Onboarding/Offboarding Best Practices</h2>
        <div className="practices-grid">
          <div className="practice-card">
            <h3>🔐 Pre-Onboarding</h3>
            <ul>
              <li>Verify role and access requirements</li>
              <li>Prepare necessary accounts and access</li>
              <li>Schedule security training</li>
            </ul>
          </div>

          <div className="practice-card">
            <h3>✅ During Onboarding</h3>
            <ul>
              <li>Grant access per principle of least access</li>
              <li>Verify equipment received</li>
              <li>Complete security training</li>
              <li>Obtain policy acknowledgment</li>
            </ul>
          </div>

          <div className="practice-card">
            <h3>🚪 Pre-Offboarding</h3>
            <ul>
              <li>Conduct knowledge transfer</li>
              <li>Backup critical data</li>
              <li>Plan asset return process</li>
            </ul>
          </div>

          <div className="practice-card">
            <h3>🔄 During Offboarding</h3>
            <ul>
              <li>Immediately revoke all access</li>
              <li>Collect all company equipment</li>
              <li>Transfer data ownership</li>
              <li>Conduct exit interview</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserLifecycle;
